import React, { Fragment } from 'react';
import {
  RetailTalentTreeFullEntry,
  RetailWarcraftTalentTreeBlueprint,
  RetailWarcraftTalentTreeBuildNode,
  RetailWarcraftTalentTreeNodeType,
} from '@resources/js/typings/viewModels/retailWarcraftTalentTreeBuildNode';
import TalentTreeNode from '@resources/js/components/TalentTreeNode/TalentTreeNode';
import useGame from '@resources/js/contexts/useGame';

type TreeNode = RetailWarcraftTalentTreeBuildNode;

type TreeRow = TreeNode[];
export type Tree = TreeRow[];

type TreeWidth = { width: number; leftOffset: number };

export function treeWidth(tree: Tree): TreeWidth {
  const { width, leftOffset } = tree
    .flatMap((row) => row)
    .reduce(
      (acc, { node: { posX } }) => {
        if (posX > acc.width) {
          acc.width = posX;
        }

        if (posX < acc.leftOffset) {
          acc.leftOffset = posX;
        }

        return acc;
      },
      { width: 0, leftOffset: Infinity }
    );

  return { width: width - leftOffset, leftOffset };
}

type SvgGridProps = Pick<
  StandardTreeProps,
  'tree' | 'expanded' | 'minWidthLarge'
> &
  TreeWidth & {
    rows: number;
  };

export function SvgGrid({
  tree,
  expanded,
  rows,
  width,
  leftOffset,
  minWidthLarge,
}: SvgGridProps): JSX.Element {
  const byId = tree.reduce<Record<number, { row: number; posX: number }>>(
    (acc, row) => {
      row.forEach((node) => {
        acc[node.node.nodeId] = {
          row: node.node.row,
          posX: node.node.posX,
        };
      });

      return acc;
    },
    {}
  );

  return (
    <svg
      className='talent-tree__implementation__svg'
      xmlns='http://www.w3.org/2000/svg'
    >
      {tree.map((row, index) => {
        if (!expanded && index > (minWidthLarge ? 1 : 2)) {
          return null;
        }

        return (
          <Fragment key={index}>
            {row.flatMap((node) => {
              return node.node.childNodes
                .filter((nodeId) => byId[nodeId] !== undefined)
                .map((nodeId, index) => {
                  const { row, posX } = byId[nodeId];

                  const x1 = ((node.node.posX - leftOffset) / width) * 100;
                  const y1 = (node.node.row / rows) * 100;

                  const x2 = ((posX - leftOffset) / width) * 100;
                  const y2 = (row / rows) * 100;

                  return (
                    <line
                      key={`${nodeId}-${node.node.row}-${node.node.posX}-${index}`}
                      x1={`${x1}%`}
                      y1={`${y1}%`}
                      x2={`${x2}%`}
                      y2={`${y2}%`}
                    />
                  );
                });
            })}
          </Fragment>
        );
      })}
    </svg>
  );
}

type NodesProps = Pick<
  StandardTreeProps,
  'tree' | 'expanded' | 'minWidthLarge' | 'spellModifierId'
> & {
  leftOffset: number;
  treeWidth: number;
};

export function Nodes({
  tree,
  expanded,
  minWidthLarge,
  spellModifierId,
  leftOffset,
  treeWidth,
}: NodesProps): JSX.Element {
  return (
    <div className='talent-tree__implementation__nodes'>
      {tree.map((row, index) => {
        if (!expanded && index > (minWidthLarge ? 2 : 3)) {
          return null;
        }

        return (
          <Fragment key={index}>
            {row.map((node) => {
              return (
                <div
                  key={node.node.nodeId}
                  className='talent-tree__implementation__nodes--item'
                  style={
                    {
                      '--row': node.node.row,
                      '--posX': `${
                        ((node.node.posX - leftOffset) / treeWidth) * 100
                      }%`,
                    } as React.CSSProperties
                  }
                >
                  {node.node.type ===
                  RetailWarcraftTalentTreeNodeType.Choice ? (
                    <TalentTreeNode
                      node={node}
                      showChoices
                      tooltipClassName={
                        'talent-tree__implementation__choice-tooltip'
                      }
                      key={`${node.node.nodeId}-entry`}
                      spellModifierId={spellModifierId}
                    />
                  ) : (
                    <TalentTreeNode
                      node={node}
                      key={`${node.node.nodeId}-entry`}
                      spellModifierId={spellModifierId}
                    />
                  )}
                </div>
              );
            })}
          </Fragment>
        );
      })}
    </div>
  );
}

interface StandardTreeProps extends RetailTalentTreeFullEntry {
  expanded: boolean;
  kind: 'spec' | 'class';
  minWidthLarge: boolean;
  labels: RetailWarcraftTalentTreeBlueprint['labels'];
  descriptionClassName: string;
  exportCode: string | undefined | null;
  isDisplayingPercent?: boolean;
  spellModifierId: number;
}

const NODE_SCALE = 400;

/**
 * A class or spec tree. The "standard" options.
 */
export function StandardTree({
  tree,
  artwork,
  kind,
  expanded,
  minWidthLarge,
  labels,
  iconClassName,
  descriptionClassName,
  exportCode,
  isDisplayingPercent,
  spellModifierId,
}: StandardTreeProps) {
  const rows = tree[tree.length - 1][0].node.row;

  const widthData = treeWidth(tree);

  return (
    <div
      data-kind={kind}
      className={`talent-tree__container talent-tree__container--${kind}`}
      style={
        {
          '--rows': rows,
          '--min-cols': widthData.width / NODE_SCALE,
        } as React.CSSProperties
      }
    >
      <div
        className='talent-tree__container--height-limiter'
        data-expanded={expanded}
        data-kind={kind}
        data-exportable={exportCode !== null}
      >
        <div
          className={`talent-tree__artwork talent-tree__artwork--${kind}`}
          style={{
            backgroundImage: `url(${artwork})`,
          }}
        />
        <Description
          iconClassName={iconClassName}
          kind={kind}
          labels={labels}
          descriptionClassName={descriptionClassName}
        />
        <div
          className={`talent-tree__implementation ${
            expanded ? 'talent-tree__implementation--expanded' : ''
          } ${
            isDisplayingPercent ? 'talent-tree__implementation--heat-map' : ''
          }`}
          data-expanded={expanded}
        >
          <SvgGrid
            tree={tree}
            expanded={expanded}
            rows={rows}
            {...widthData}
            minWidthLarge={minWidthLarge}
          />
          <Nodes
            tree={tree}
            expanded={expanded}
            minWidthLarge={minWidthLarge}
            leftOffset={widthData.leftOffset}
            treeWidth={widthData.width}
            spellModifierId={spellModifierId}
          />
        </div>
      </div>
    </div>
  );
}

type DescriptionProps = Pick<
  StandardTreeProps,
  'kind' | 'iconClassName' | 'labels' | 'descriptionClassName'
>;

function Description({
  iconClassName,
  kind,
  labels,
  descriptionClassName,
}: DescriptionProps): JSX.Element {
  const game = useGame();

  return (
    <div
      className={`talent-tree__description ${
        kind === 'class'
          ? 'talent-tree__description--class'
          : 'talent-tree__description--spec'
      }`}
    >
      <div className='talent-tree__description__icon'>
        <img
          className={`sprite ${iconClassName}`}
          src={game?.actorsSprite}
          loading='lazy'
          alt=''
        />
      </div>
      <span className='talent-tree__description__label'>
        <span
          className={`talent-tree__description__label--kind ${descriptionClassName}`}
        >
          {kind === 'class' ? labels.class : labels.spec}
        </span>
        <span className='talent-tree__description__label--generic'>
          {labels.talents}
        </span>
      </span>
    </div>
  );
}
